import { useState } from 'react';
import { Button, ButtonGroup, InlineStack, Paragraph, Stack, TextArea } from '@customerio/pluma-components/react';
import { isEmptyString } from '@/utils/string';
import { EditTrigger } from './edit-trigger';
import { RowClickGuard } from './row-click-guard';
import { descriptionCounter, descriptionError } from '../utils/editing';
import type { DataIndexAnalyticsType } from '../utils/types';
import { GenerateDescriptionButton } from './generate-description-button';

/**
 * A row's description, written in place.
 *
 * The description is what the next person reading this list learns about the
 * row, so it is editable from the list rather than only from the row's own
 * page. A row nobody may describe shows a dash: an invitation the page would
 * refuse is worse than none.
 */

const NO_PERMISSION_TOOLTIP = 'Only workspace admins can edit attribute descriptions.';

export type DescriptionEditorProps = {
  workspaceId: number;
  /** What the row is called, which is also what the model is asked about. */
  name: string;
  type: DataIndexAnalyticsType;
  objectTypeId?: number;
  description: string | null;
  /** False for a reserved name, or for a viewer without the permission. */
  canEdit: boolean;
  /** Whether the viewer would be allowed if the row were not reserved. */
  hasPermission: boolean;
  /** Resolves to whether the save went through; a failure keeps the box open. */
  onSave: (description: string) => Promise<boolean>;
  /**
   * Set while this row is one of several being edited together. The value
   * then lives outside the cell, because one Save covers every row, so the
   * box is always open and has no buttons of its own.
   */
  bulk?: { value: string; onChange: (description: string) => void };
};

export function DescriptionEditor({
  workspaceId,
  name,
  type,
  objectTypeId,
  description,
  canEdit,
  hasPermission,
  onSave,
  bulk,
}: DescriptionEditorProps) {
  const [ownDraft, setOwnDraft] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // In a bulk edit the value lives outside the cell; on its own the cell
  // holds it, and `null` is what "not editing" means.
  // A row nobody may edit shows its description whichever mode the table is
  // in; a bulk edit never contains one, but the component says so itself.
  const draft = canEdit ? (bulk?.value ?? ownDraft) : null;
  const setDraft = (next: string) => (bulk == null ? setOwnDraft(next) : bulk.onChange(next));
  const stopEditing = () => setOwnDraft(null);

  if (draft == null) {
    return (
      <RowClickGuard>
        <DescriptionTrigger
          description={description}
          canEdit={canEdit}
          hasPermission={hasPermission}
          onStart={() => setDraft(description ?? '')}
        />
      </RowClickGuard>
    );
  }

  const error = descriptionError(draft);

  const save = async () => {
    if (error != null || isSaving) {
      return;
    }
    setIsSaving(true);
    const saved = await onSave(draft);
    setIsSaving(false);
    if (saved) {
      stopEditing();
    }
  };

  return (
    <RowClickGuard>
      <InlineStack gap="100">
        <Stack gap="050" flexGrow="1">
          <TextArea
            ariaLabel={`Description for ${name}`}
            autoFocus={true}
            rows={3}
            value={draft}
            placeholder="Description…"
            isInvalid={error != null}
            error={error}
            isDisabled={isSaving}
            onInput={(event) => setDraft(event.target.value)}
          />
          {error == null ? (
            <Paragraph size="xs" variant={isEmptyString(draft) ? 'secondary' : undefined}>
              {descriptionCounter(draft)}
            </Paragraph>
          ) : null}
          <GenerateDescriptionButton
            workspaceId={workspaceId}
            type={type}
            name={name}
            objectTypeId={objectTypeId}
            isDisabled={error != null || isSaving}
            // Guarded: a generation that lands after Cancel must not reopen
            // the box with text nobody asked to keep.
            onGenerated={(generated) =>
              bulk == null ? setOwnDraft((current) => (current == null ? null : generated)) : bulk.onChange(generated)
            }
          />
        </Stack>
        {bulk != null ? null : (
          <ButtonGroup groupVariant="spaced">
            <Button
              variant="primary"
              size="sm"
              icon="check"
              isIconOnly
              aria-label="Save description"
              isDisabled={error != null}
              isLoading={isSaving}
              onClick={() => void save()}
            />
            <Button
              variant="secondary"
              size="sm"
              icon="close"
              isIconOnly
              aria-label="Cancel editing"
              isDisabled={isSaving}
              onClick={stopEditing}
            />
          </ButtonGroup>
        )}
      </InlineStack>
    </RowClickGuard>
  );
}

type DescriptionTriggerProps = {
  description: string | null;
  canEdit: boolean;
  hasPermission: boolean;
  onStart: () => void;
};

/** The description as it reads when nobody is editing it. */
function DescriptionTrigger({ description, canEdit, hasPermission, onStart }: DescriptionTriggerProps) {
  return (
    <EditTrigger canEdit={canEdit} hasPermission={hasPermission} tooltip={NO_PERMISSION_TOOLTIP} onStart={onStart}>
      {isEmptyString(description) ? (canEdit ? 'Add description' : '-') : description}
    </EditTrigger>
  );
}
